import { LimitDomain, LimitResult, NumericInterval } from './types';

const DECIMAL = '[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)';
const INTERVAL = new RegExp(
  `^\\s*([\\[(])\\s*(${DECIMAL})\\s*,\\s*(${DECIMAL})\\s*([\\])])\\s*$`,
);
const DECIMAL_VALUE = new RegExp(`^${DECIMAL}$`);

export function finiteDecimal(value: unknown): number | undefined {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string' || !DECIMAL_VALUE.test(value.trim()))
    return undefined;
  const number = Number(value.trim());
  return Number.isFinite(number) ? number : undefined;
}

export function parseNumericInterval(
  input: unknown,
): LimitResult<NumericInterval> {
  const match = typeof input === 'string' ? INTERVAL.exec(input) : null;
  if (!match)
    return {
      ok: false,
      issues: [
        {
          code: 'INVALID_INTERVAL',
          message: 'Expected a finite numeric interval.',
        },
      ],
    };
  const lower = Number(match[2]);
  const upper = Number(match[3]);
  const lowerInclusive = match[1] === '[';
  const upperInclusive = match[4] === ']';
  if (
    !Number.isFinite(lower) ||
    !Number.isFinite(upper) ||
    lower > upper ||
    (lower === upper && !(lowerInclusive && upperInclusive))
  ) {
    return {
      ok: false,
      issues: [
        {
          code: 'EMPTY_INTERVAL',
          message: 'Interval must contain a finite admissible value.',
        },
      ],
    };
  }
  return {
    ok: true,
    value: Object.freeze({ lower, upper, lowerInclusive, upperInclusive }),
  };
}

export function intervalContains(
  value: number,
  interval: NumericInterval,
): boolean {
  return (
    Number.isFinite(value) &&
    (value > interval.lower ||
      (interval.lowerInclusive && value === interval.lower)) &&
    (value < interval.upper ||
      (interval.upperInclusive && value === interval.upper))
  );
}

// Decimal arithmetic follows canonical JS number strings, without tolerances.
// BigInt avoids epsilon widening and overflow in decimal grid calculations.
const ZERO = BigInt(0);
const ONE = BigInt(1);
const powerOfTen = (scale: number) => BigInt('1' + '0'.repeat(scale));
function decimal(value: number): { coefficient: bigint; scale: number } {
  const [mantissa, exponent = '0'] = value.toString().toLowerCase().split('e');
  const parts = mantissa.split('.');
  const digits = parts.join('');
  const scale = (parts[1]?.length ?? 0) - Number(exponent);
  return scale < 0
    ? { coefficient: BigInt(digits) * powerOfTen(-scale), scale: 0 }
    : { coefficient: BigInt(digits), scale };
}
function ratio(
  value: number,
  step: number,
): { numerator: bigint; denominator: bigint } {
  const v = decimal(value);
  const s = decimal(step);
  return {
    numerator: v.coefficient * powerOfTen(s.scale),
    denominator: s.coefficient * powerOfTen(v.scale),
  };
}
function floorRatio(
  value: number,
  step: number,
): { index: bigint; exact: boolean } {
  const { numerator, denominator } = ratio(value, step);
  const remainder = numerator % denominator;
  const quotient = numerator / denominator;
  return {
    index: quotient - (numerator < ZERO && remainder !== ZERO ? ONE : ZERO),
    exact: remainder === ZERO,
  };
}
function gridValue(index: bigint, step: number): number {
  const s = decimal(step);
  const coefficient = index * s.coefficient;
  const negative = coefficient < ZERO;
  const digits = (negative ? -coefficient : coefficient)
    .toString()
    .padStart(s.scale + 1, '0');
  const text = s.scale
    ? `${digits.slice(0, -s.scale)}.${digits.slice(-s.scale)}`
    : digits;
  return Number(`${negative ? '-' : ''}${text}`);
}
export function containsLimitValue(
  value: number,
  domain: LimitDomain,
): boolean {
  if (!intervalContains(value, domain.interval)) return false;
  if (domain.step === undefined) return true;
  if (!Number.isFinite(domain.step) || domain.step <= 0) return false;
  const { numerator, denominator } = ratio(value, domain.step);
  return numerator % denominator === ZERO;
}
export function admissibleDomainEnds(domain: LimitDomain): {
  minimum?: number;
  maximum?: number;
} {
  const { lower, upper, lowerInclusive, upperInclusive } = domain.interval;
  if (domain.step === undefined)
    return {
      minimum: lowerInclusive ? lower : undefined,
      maximum: upperInclusive ? upper : undefined,
    };
  const low = floorRatio(lower, domain.step);
  const high = floorRatio(upper, domain.step);
  const lowerIndex = low.index + (!low.exact || !lowerInclusive ? ONE : ZERO);
  const upperIndex = high.index - (high.exact && !upperInclusive ? ONE : ZERO);
  if (lowerIndex > upperIndex) return {};
  const minimum = gridValue(lowerIndex, domain.step);
  const maximum = gridValue(upperIndex, domain.step);
  return {
    minimum: containsLimitValue(minimum, domain) ? minimum : undefined,
    maximum: containsLimitValue(maximum, domain) ? maximum : undefined,
  };
}
